const dependencyLoads = new Map();

function loadDependency(globalName, url) {
  if (window[globalName]) return Promise.resolve(window[globalName]);
  if (dependencyLoads.has(globalName)) return dependencyLoads.get(globalName);

  const promise = new Promise((resolve, reject) => {
    let script = [...document.scripts].find((candidate) => candidate.src === url);
    const ownsScript = !script;

    if (!script) {
      script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.luxdeiDependency = globalName;
    }

    let timeoutId;
    let pollId;

    const finish = (error) => {
      window.clearTimeout(timeoutId);
      window.clearInterval(pollId);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);

      if (error) {
        if (ownsScript) script.remove();
        reject(error);
        return;
      }

      resolve(window[globalName]);
    };

    const handleLoad = () => {
      finish(window[globalName] ? null : new Error(`${globalName} non disponibile dopo il caricamento`));
    };
    const handleError = () => finish(new Error(`Impossibile caricare ${globalName}`));

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    pollId = window.setInterval(() => {
      if (window[globalName]) finish();
    }, 100);
    timeoutId = window.setTimeout(() => finish(new Error(`Timeout durante il caricamento di ${globalName}`)), 10000);

    if (ownsScript) document.head.appendChild(script);
  });

  dependencyLoads.set(globalName, promise);
  promise.catch(() => dependencyLoads.delete(globalName));
  return promise;
}

async function loadGsap(root) {
  await loadDependency('gsap', root.dataset.gsapUrl);
  await loadDependency('ScrollTrigger', root.dataset.scrolltriggerUrl);

  window.gsap.registerPlugin(window.ScrollTrigger);
}

class LuxdeiBentoScroll extends HTMLElement {
  #stage;
  #grid;
  #mainTile;
  #secondaryTiles;
  #content;
  #overlay;
  #label;
  #scroller;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #scrollContainerQuery = window.matchMedia('(min-width: 990px)');
  #context;
  #trigger;
  #resizeObserver;
  #resizeTimer;
  #refreshFrame;
  #generation = 0;
  #lastWidth = 0;
  #lastHeight = 0;

  connectedCallback() {
    this.#stage = this.querySelector('[data-bento-stage]');
    this.#grid = this.querySelector('[data-bento-grid]');
    this.#mainTile = this.querySelector('[data-bento-main]');
    this.#secondaryTiles = [...this.querySelectorAll('[data-bento-secondary]')];
    this.#content = this.querySelector('[data-bento-content]');
    this.#overlay = this.querySelector('[data-bento-overlay]');
    this.#label = this.querySelector('[data-bento-label]');
    this.#setScroller();

    if (!this.#stage || !this.#grid || !this.#mainTile || !this.#content || !this.#overlay) {
      this.classList.remove('luxdei-bento--pending');
      this.classList.add('luxdei-bento--static');
      this.dataset.bentoState = 'static';
      return;
    }

    this.#motionQuery.addEventListener('change', this.#handleMediaChange);
    this.#scrollContainerQuery.addEventListener('change', this.#handleMediaChange);
    this.addEventListener('load', this.#handleAssetLoad, true);

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#stage);

    this.#initialize();
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#motionQuery.removeEventListener('change', this.#handleMediaChange);
    this.#scrollContainerQuery.removeEventListener('change', this.#handleMediaChange);
    this.removeEventListener('load', this.#handleAssetLoad, true);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    window.clearTimeout(this.#resizeTimer);
    window.cancelAnimationFrame(this.#refreshFrame);
    this.#destroyAnimation();
  }

  #handleMediaChange = () => {
    this.#setScroller();
    this.#initialize();
  };

  #setScroller() {
    this.#scroller = this.#scrollContainerQuery.matches ? this.closest('.page-wrapper') || window : window;
  }

  #handleAssetLoad = () => this.#scheduleRefresh();

  #handleResize = () => {
    const width = this.clientWidth;
    const height = this.#stage?.clientHeight ?? 0;

    if (Math.abs(width - this.#lastWidth) < 2 && Math.abs(height - this.#lastHeight) < 2) return;

    window.clearTimeout(this.#resizeTimer);
    this.#resizeTimer = window.setTimeout(() => {
      if (!this.isConnected || this.classList.contains('luxdei-bento--static')) return;
      this.#buildAnimation();
    }, 180);
  };

  async #initialize() {
    const generation = ++this.#generation;
    this.#destroyAnimation();

    if (this.#motionQuery.matches) {
      this.#setStatic('reduced-motion');
      return;
    }

    this.dataset.bentoState = 'loading';

    try {
      await loadGsap(this);
      if (!this.isConnected || generation !== this.#generation) return;
      this.#buildAnimation();
    } catch (error) {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#setStatic('dependency-error');
      console.warn('[Luxdei Bento]', error);
    }
  }

  #buildAnimation() {
    if (!window.gsap || !window.ScrollTrigger) {
      this.#setStatic('missing-dependency');
      return;
    }

    this.#destroyAnimation();
    this.classList.remove('luxdei-bento--pending');
    this.classList.remove('luxdei-bento--static');
    delete this.dataset.bentoStaticReason;
    this.dataset.bentoState = 'ready';

    const { gsap, ScrollTrigger } = window;
    const scrollMultiplier = Math.max(1, Number(this.dataset.scrollDistance) / 100 || 2);
    const pinOffset = Math.max(0, Number(this.dataset.pinOffset) || 0);

    this.style.setProperty(
      '--luxdei-bento-track-height',
      `${this.#stage.clientHeight * (1 + scrollMultiplier)}px`
    );

    this.#context = gsap.context(() => {
      const stageRect = this.#stage.getBoundingClientRect();
      const mainRect = this.#mainTile.getBoundingClientRect();
      const mainCenterX = mainRect.left + mainRect.width / 2;
      const mainCenterY = mainRect.top + mainRect.height / 2;
      const stageCenterX = stageRect.left + stageRect.width / 2;
      const stageCenterY = stageRect.top + stageRect.height / 2;
      const mainScale = Math.max(stageRect.width / mainRect.width, stageRect.height / mainRect.height) * 1.01;

      const secondaryExit = (tile) => {
        const slot = Number(tile.dataset.slot);

        if (slot === 1 || slot === 5) return { x: '-24vw', y: slot === 1 ? '-8vh' : '8vh' };
        if (slot === 2) return { x: 0, y: '-24vh' };
        if (slot === 4 || slot === 6) return { x: '24vw', y: slot === 4 ? '-8vh' : '8vh' };

        return { x: 0, y: '20vh' };
      };

      gsap.set(this.#mainTile, {
        transformOrigin: 'center center',
        force3D: true,
      });

      const timeline = gsap.timeline({
        paused: true,
        onUpdate: () => {
          this.#content.toggleAttribute('inert', timeline.progress() < 0.58);
        },
      });

      timeline
        .to(
          this.#mainTile,
          {
            x: stageCenterX - mainCenterX,
            y: stageCenterY - mainCenterY,
            scale: mainScale,
            borderRadius: 0,
            duration: 1,
            ease: 'power2.inOut',
          },
          0
        )
        .to(
          this.#secondaryTiles,
          {
            x: (index, tile) => secondaryExit(tile).x,
            y: (index, tile) => secondaryExit(tile).y,
            opacity: 0,
            scale: 0.94,
            duration: 0.62,
            stagger: 0.025,
            ease: 'power2.in',
          },
          0.18
        );

      if (this.#label) {
        timeline.to(this.#label, { autoAlpha: 0, duration: 0.25 }, 0.42);
      }

      timeline
        .to(
          this.#overlay,
          {
            opacity: Number.parseFloat(getComputedStyle(this).getPropertyValue('--luxdei-bento-overlay-opacity')) || 0,
            duration: 0.36,
          },
          0.64
        )
        .fromTo(
          this.#content,
          { y: 48, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 0.34, ease: 'power2.out' },
          0.72
        )
        .to({}, { duration: 0.18 });

      this.#trigger = ScrollTrigger.create({
        id: `luxdei-bento-${this.dataset.sectionId}`,
        trigger: this,
        scroller: this.#scroller,
        animation: timeline,
        start: () => `top ${pinOffset}px`,
        end: () => `+=${this.#stage.clientHeight * scrollMultiplier}`,
        scrub: 0.4,
        invalidateOnRefresh: true,
      });

      timeline.totalProgress(this.#trigger.progress, false);
      this.#content.toggleAttribute('inert', timeline.progress() < 0.58);
    }, this);

    this.#lastWidth = this.clientWidth;
    this.#lastHeight = this.#stage.clientHeight;
    this.#scheduleRefresh();
  }

  #destroyAnimation() {
    this.#trigger?.kill(true);
    this.#trigger = undefined;
    this.#context?.revert();
    this.#context = undefined;
    this.style.removeProperty('--luxdei-bento-track-height');
    this.#content?.removeAttribute('inert');
  }

  #setStatic(reason) {
    this.#destroyAnimation();
    this.classList.remove('luxdei-bento--pending');
    this.classList.add('luxdei-bento--static');
    this.dataset.bentoState = 'static';
    this.dataset.bentoStaticReason = reason;
    this.#scheduleRefresh();
  }

  #scheduleRefresh() {
    window.cancelAnimationFrame(this.#refreshFrame);
    this.#refreshFrame = window.requestAnimationFrame(() => window.ScrollTrigger?.refresh());
  }
}

if (!customElements.get('luxdei-bento-scroll')) {
  customElements.define('luxdei-bento-scroll', LuxdeiBentoScroll);
}
